import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Trash2, Plus, Minus, ArrowLeft } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { getProxiedImageUrl } from '../utils/helpers';

const Cart = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { cart, removeFromCart, updateQuantity, clearCart, getCartTotal, getCartCount } = useCart();

  if (cart.length === 0) {
    return (
      <div className="dark-container pt-20 min-h-screen">
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <ShoppingCart size={64} className="mx-auto mb-6 opacity-20" />
          <h1 className="display-medium mb-4">
            {language === 'it' ? 'Il Tuo Carrello è Vuoto' : 'Your Cart is Empty'}
          </h1>
          <p className="body-large mb-8" style={{ color: 'var(--text-secondary)' }}>
            {language === 'it' 
              ? 'Aggiungi prodotti al carrello per iniziare!' 
              : 'Add products to cart to get started!'}
          </p>
          <Link to="/products" className="btn-primary">
            {language === 'it' ? 'Scopri i Prodotti' : 'Discover Products'}
          </Link>
        </div>
      </div>
    );
  }

  const shippingCost = getCartTotal() >= 150 ? 0 : 6.90;
  const total = getCartTotal() + shippingCost;

  return (
    <div className="dark-container pt-20 min-h-screen">
      {/* Header */}
      <section className="py-6 sm:py-8 px-4 sm:px-6 lg:px-[7.6923%]">
        <div className="max-w-[1400px] mx-auto">
          <button 
            onClick={() => navigate('/products')}
            className="flex items-center gap-2 text-white/80 hover:text-white transition-colors mb-4 sm:mb-6 text-sm sm:text-base"
          >
            <ArrowLeft size={18} />
            {language === 'it' ? 'Continua lo Shopping' : 'Continue Shopping'}
          </button>
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-0">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold">
              {language === 'it' ? 'Carrello' : 'Shopping Cart'} ({getCartCount()})
            </h1>
            <button 
              onClick={clearCart}
              className="text-red-400 hover:text-red-300 text-sm underline self-start sm:self-auto"
            >
              {language === 'it' ? 'Svuota Carrello' : 'Clear Cart'}
            </button>
          </div>
        </div>
      </section>

      {/* Cart Content */}
      <section className="py-8 px-6 lg:px-[7.6923%]">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {cart.map((item) => (
                <div key={item.sku} className="dark-card p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                    {/* Image */}
                    <div className="w-full sm:w-32 h-40 sm:h-32 flex-shrink-0 bg-white/5 overflow-hidden rounded">
                      {item.image ? (
                        <img
                          src={getProxiedImageUrl(item.image)}
                          alt={language === 'it' ? item.name_it : item.name_en}
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-white/20 text-xs">
                          No Image
                        </div>
                      )}
                    </div>

                    {/* Info Container */}
                    <div className="flex-1 min-w-0">
                      {/* Title and Remove Button Row */}
                      <div className="flex justify-between items-start gap-3 mb-3">
                        <h3 className="text-base sm:text-xl font-semibold leading-tight flex-1 break-words">
                          {language === 'it' ? item.name_it : item.name_en}
                        </h3>
                        
                        {/* Remove Button - More prominent */}
                        <button
                          onClick={() => removeFromCart(item.sku)}
                          className="flex-shrink-0 p-2 sm:p-1 bg-red-500/20 hover:bg-red-500/30 rounded transition-colors"
                          title={language === 'it' ? 'Rimuovi dal carrello' : 'Remove from cart'}
                        >
                          <Trash2 size={20} className="text-red-400" />
                        </button>
                      </div>

                      {/* Price */}
                      <div className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4" style={{ color: 'var(--brand-primary)' }}>
                        €{item.price.toFixed(2)}
                      </div>

                      {/* Quantity Controls */}
                      <div className="flex flex-wrap items-center gap-3 sm:gap-4 mb-3 sm:mb-0">
                        <span className="text-sm" style={{ color: 'var(--text-muted)' }}>
                          {language === 'it' ? 'Quantità:' : 'Quantity:'}
                        </span>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => updateQuantity(item.sku, item.quantity - 1)}
                            className="w-8 h-8 bg-white/10 hover:bg-white/20 transition-colors flex items-center justify-center rounded"
                            disabled={item.quantity <= 1}
                          >
                            <Minus size={16} />
                          </button>
                          <span className="w-12 text-center font-semibold">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.sku, item.quantity + 1)}
                            className="w-8 h-8 bg-white/10 hover:bg-white/20 transition-colors flex items-center justify-center rounded"
                            disabled={item.quantity >= item.maxStock}
                          >
                            <Plus size={16} />
                          </button>
                        </div>
                        {item.quantity >= item.maxStock && (
                          <span className="text-xs text-red-400 w-full sm:w-auto">
                            ({language === 'it' ? 'Max disponibile' : 'Max available'})
                          </span>
                        )}
                      </div>

                      {/* Subtotal */}
                      <div className="mt-3 sm:mt-4 text-base sm:text-lg font-semibold">
                        {language === 'it' ? 'Subtotale:' : 'Subtotal:'} €{(item.price * item.quantity).toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="dark-card p-6 sticky top-24">
                <h2 className="text-2xl font-semibold mb-6">
                  {language === 'it' ? 'Riepilogo Ordine' : 'Order Summary'}
                </h2>

                <div className="space-y-4 mb-6">
                  <div className="flex justify-between">
                    <span>{language === 'it' ? 'Subtotale' : 'Subtotal'}</span>
                    <span className="font-semibold">€{getCartTotal().toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{language === 'it' ? 'Spedizione' : 'Shipping'}</span>
                    <span className="font-semibold">
                      {shippingCost === 0 ? (
                        <span style={{ color: 'var(--brand-primary)' }}>
                          {language === 'it' ? 'GRATIS' : 'FREE'}
                        </span>
                      ) : (
                        `€${shippingCost.toFixed(2)}`
                      )}
                    </span>
                  </div>
                  
                  {getCartTotal() < 150 && (
                    <div className="text-sm p-3 bg-orange-500/10 border border-orange-500/30">
                      {language === 'it' 
                        ? `Aggiungi €${(150 - getCartTotal()).toFixed(2)} per la spedizione gratuita (solo Italia)!`
                        : language === 'es'
                        ? `¡Añade €${(150 - getCartTotal()).toFixed(2)} para envío gratis (solo Italia)!`
                        : `Add €${(150 - getCartTotal()).toFixed(2)} for free shipping (Italy only)!`}
                    </div>
                  )}

                  <div className="border-t border-white/25 pt-4 flex justify-between text-2xl font-bold">
                    <span>{language === 'it' ? 'Totale' : 'Total'}</span>
                    <span style={{ color: 'var(--brand-primary)' }}>€{total.toFixed(2)}</span>
                  </div>
                </div>

                <button 
                  onClick={() => navigate('/checkout')}
                  className="btn-primary w-full text-xl py-4 mb-4"
                >
                  {language === 'it' ? 'Procedi al Checkout' : 'Proceed to Checkout'}
                </button>

                <div className="text-xs text-center" style={{ color: 'var(--text-muted)' }}>
                  {language === 'it' 
                    ? 'Pagamento sicuro con Stripe, PayPal, Contrassegno o Bonifico'
                    : 'Secure payment with Stripe, PayPal, COD or Bank Transfer'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Cart;
